/**
 * What the exit banner says, and why a large exit arrives in waves.
 *
 * Pure and React-free so the wording is testable: this is the text a trader
 * reads while their money is moving, and "is the sentence true for this leg
 * count" is a question worth an assertion rather than an eyeball.
 */

/**
 * What the exit banner says.
 *
 * Two parts rather than one sentence, because they answer two different
 * questions: `headline` is WHAT IS HAPPENING, which a trader reads at a
 * glance mid-session, and `detail` is WHY IT TAKES THE TIME IT TAKES,
 * which they read once and then stop needing.
 */
export interface ExitNotice {
  readonly headline: string
  /** Absent when there is nothing to explain. */
  readonly detail?: string
}

/**
 * The exchange's order-rate ceiling, in orders per second.
 *
 * SEBI's algo framework registers an algorithm against an order-rate
 * threshold of ten per second, and the dispatch loop paces a basket to stay
 * under it rather than firing a thirty-leg exit at once.
 *
 * COPY ONLY. Nothing is rate-limited by this constant — the budget lives on
 * the `BrokerProvider` row and is enforced server-side by the token bucket.
 * This is the number we TELL the trader, and it is the rule itself rather
 * than our configured pace, which sits below it.
 */
export const EXCHANGE_ORDERS_PER_SEC = 10

/**
 * Above this many legs an exit is released in more than one batch, so the
 * wait is worth explaining.
 *
 * Mirrors the server's `MAX_CONCURRENT_IN_PHASE`. Deliberately NOT imported:
 * this decides whether a sentence appears, never whether an order is sent,
 * so the two are allowed to drift. The worst case if they do is an
 * explanation shown for a basket that did not need it.
 */
export const EXPLAIN_PACING_ABOVE_LEGS = 4

/**
 * Why a large exit arrives in waves.
 *
 * Said only when it is true. On a two-leg exit this sentence would be noise,
 * and a banner that always explains itself stops being read.
 */
export function pacingDetail(legCount: number): string | undefined {
  if (legCount <= EXPLAIN_PACING_ABOVE_LEGS) return undefined
  return (
    `All ${legCount} orders are queued and releasing in paced batches to stay within ` +
    `the exchange limit of ${EXCHANGE_ORDERS_PER_SEC} orders per second. ` +
    `Nothing is stalled — each batch is sent as soon as the rate allows.`
  )
}
